import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'
import { friendlyMessage } from './errorMessage'

/**
 * Axios instance shared by both Super Admin Console and School Admin Portal.
 *
 * Concerns layered here (Tech Stack §4.5):
 *  - Bearer auth via in-memory token from the Pinia auth store (never localStorage).
 *  - `X-Correlation-Id` header for request tracing.
 *  - `X-Tenant-Id` header scoped to the active school (School Admin) — omitted for
 *    platform-level calls (Super Admin).
 *  - Automatic silent refresh on 401 using the HttpOnly refresh cookie, with a
 *    single-flight guard so concurrent 401s share one refresh round-trip.
 *  - Error normalization into a small ApiError shape the UI can rely on.
 */

export interface ApiError {
  status: number
  code: string
  message: string
  details?: unknown
  correlationId?: string
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // send HttpOnly refresh cookie on refresh calls.
  timeout: 30_000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
})

function generateCorrelationId(): string {
  // Prefer crypto.randomUUID when present; fall back to a short random hex id.
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return Array.from({ length: 4 }, () =>
    Math.floor(Math.random() * 0xffffffff)
      .toString(16)
      .padStart(8, '0'),
  ).join('-')
}

// Lazy import to avoid a circular dep at module-eval time.
// Pinia stores are defined in `@/stores/*` and import this module.
async function getAuthTokens(): Promise<{ accessToken: string | null; tenantId: string | null }> {
  const mod = await import('@/stores/auth')
  const store = mod.useAuthStore()
  return { accessToken: store.accessToken, tenantId: store.activeTenantId }
}

apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const { accessToken, tenantId } = await getAuthTokens()

  config.headers = config.headers ?? {}
  config.headers['X-Correlation-Id'] = generateCorrelationId()

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }
  if (tenantId) {
    config.headers['X-Tenant-Id'] = tenantId
  }

  // FormData (file uploads) must NOT carry a hand-set Content-Type. The instance
  // default is `application/json`, and callers that set `multipart/form-data`
  // explicitly omit the required boundary — either way the server can't parse the
  // parts. Drop it so the browser emits `multipart/form-data; boundary=…` itself.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    const headers = config.headers as unknown as {
      delete?: (name: string) => void
      [key: string]: unknown
    }
    if (typeof headers.delete === 'function') {
      headers.delete('Content-Type')
    } else {
      delete headers['Content-Type']
    }
  }
  return config
})

// Single-flight refresh guard.
let refreshInFlight: Promise<string | null> | null = null

async function attemptRefresh(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const mod = await import('@/stores/auth')
        const store = mod.useAuthStore()
        return await store.refresh()
      } catch {
        return null
      } finally {
        // Release lock on next microtask so concurrent awaits resolve first.
        queueMicrotask(() => {
          refreshInFlight = null
        })
      }
    })()
  }
  return refreshInFlight
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ code?: string; message?: string; details?: unknown; reference?: string }>) => {
    const original = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined
    const status = error.response?.status ?? 0
    const url = original?.url ?? ''

    // The refresh call sets header X-Skip-Auth=1. Never try to refresh a
    // refresh — that deadlocks the single-flight guard when the router
    // guard and the interceptor both see a 401 from /v1/auth/refresh.
    const isAuthCall =
      url.endsWith('/v1/auth/refresh') ||
      url.endsWith('/v1/auth/login') ||
      url.endsWith('/v1/auth/logout') ||
      (original?.headers?.['X-Skip-Auth'] === '1')

    // Attempt one silent refresh on 401, then retry the original request.
    if (status === 401 && original && !original._retry && !isAuthCall) {
      original._retry = true
      const newToken = await attemptRefresh()
      if (newToken) {
        original.headers = original.headers ?? {}
        ;(original.headers as Record<string, string>).Authorization = `Bearer ${newToken}`
        return apiClient.request(original)
      }
    }

    const normalized: ApiError = {
      status,
      code: error.response?.data?.code ?? error.code ?? 'UNKNOWN',
      // Never error.message: that is axios's "Request failed with status code 500".
      message: friendlyMessage(status, error.response?.data?.message, error.code),
      details: error.response?.data?.details,
      correlationId:
        (error.response?.headers?.['x-correlation-id'] as string | undefined) ??
        (error.config?.headers?.['X-Correlation-Id'] as string | undefined),
    }
    return Promise.reject(normalized)
  },
)
